import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { getMovements } from '../../services/api';
import { toast } from 'sonner';
import { History, ArrowDownRight, PackageMinus, Truck } from 'lucide-react';

const TYPE_META = {
  pick: { label: 'Pick', cls: 'bg-blue-100 text-blue-700', Icon: ArrowDownRight },
  consume: { label: 'Consume', cls: 'bg-orange-100 text-orange-700', Icon: PackageMinus },
  dispatch: { label: 'Dispatch', cls: 'bg-purple-100 text-purple-700', Icon: Truck },
};

const FILTERS = [
  { key: '', label: 'All' },
  { key: 'pick', label: 'Picks' },
  { key: 'consume', label: 'Consumes' },
  { key: 'dispatch', label: 'Dispatches' },
];

const locLabel = (loc) => loc ? [loc.warehouse, loc.zone, loc.aisle, loc.rack, loc.shelf, loc.bin].filter(Boolean).join(' · ') : '-';

const fmtDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const MovementTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMovements(filter || undefined);
      setRows(res.data);
    } catch (e) {
      toast.error('Failed to load movement history');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="space-y-4" data-testid="movement-tab-content">
      <Card><CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <p className="font-medium flex items-center gap-2"><History size={16} className="text-accent" />Stock Movement History</p>
          <p className="text-sm text-muted-foreground">Every pick, consume and dispatch that left your stock, newest first</p>
        </div>
        <div className="flex gap-1">
          {FILTERS.map((f) => (
            <Button key={f.key || 'all'} size="sm" variant={filter === f.key ? 'default' : 'outline'} onClick={() => setFilter(f.key)} data-testid={`movement-filter-${f.key || 'all'}`}>{f.label}</Button>
          ))}
        </div>
      </CardContent></Card>

      <Card><CardContent className="p-0">
        {loading ? (
          <div className="flex items-center justify-center h-40"><div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" /></div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-muted-foreground"><History size={40} className="mb-3 opacity-50" /><p>No stock movements yet</p><p className="text-sm mt-1">Complete a picking list, consume inventory or dispatch a stillage to see entries here</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50"><tr>
                <th className="px-4 py-3 text-left text-sm font-medium">When</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Type</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Item</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Qty</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Reference</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Location</th>
                <th className="px-4 py-3 text-left text-sm font-medium">By</th>
              </tr></thead>
              <tbody className="divide-y">
                {rows.map((m) => {
                  const meta = TYPE_META[m.type] || { label: m.type, cls: 'bg-gray-100 text-gray-700', Icon: History };
                  const Icon = meta.Icon;
                  return (
                    <tr key={m.id} className="hover:bg-muted/30" data-testid={`movement-row-${m.id}`}>
                      <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">{fmtDate(m.created_at)}</td>
                      <td className="px-4 py-3"><span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${meta.cls}`}><Icon size={12} />{meta.label}</span></td>
                      <td className="px-4 py-3"><div className="text-sm">{m.item_name || '-'}</div>{m.item_code && <div className="text-xs font-mono text-muted-foreground">{m.item_code}</div>}</td>
                      <td className="px-4 py-3 font-mono text-sm">{m.quantity} {m.unit}</td>
                      <td className="px-4 py-3 font-mono text-sm">{m.reference || '-'}</td>
                      <td className="px-4 py-3 text-sm">{locLabel(m.location)}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{m.user_name || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent></Card>
    </div>
  );
};

export default MovementTab;
